import { useId } from 'react';

import { fade, palette } from '../../common/colors';
import { darkThemeSelector, keyframes, styled } from '../../stitches.config';

export const LIVE_DOT_RING_SCALE = 2.25;

const liveDotPulse = keyframes({
  '0%': { transform: 'scale(0.8)' },
  '50%': { transform: 'scale(1)' },
  '100%': { transform: 'scale(0.8)' },
});

const liveDotRing = keyframes({
  '0%': { transform: 'scale(1)', opacity: 1 },
  '100%': { transform: `scale(${LIVE_DOT_RING_SCALE})`, opacity: 0 },
});

const LiveDotSVG = styled('svg', {
  display: 'block',
  overflow: 'visible',
  flexShrink: 0,
});
LiveDotSVG.displayName = 'LiveDotSVG';

const LiveDotPulse = styled('g', {
  variants: {
    paused: {
      true: {},
      false: {
        animation: `${liveDotPulse} 2s ease-in-out infinite`,

        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      },
    },
  },
});
LiveDotPulse.displayName = 'LiveDotPulse';

const LiveDotRing = styled('circle', {
  fill: fade(palette.green300, 0.2),
  animation: `${liveDotRing} 2s ease-in-out infinite`,

  [darkThemeSelector]: {
    fill: palette.green700,
  },

  '@media (prefers-reduced-motion: reduce)': {
    display: 'none',
  },
});
LiveDotRing.displayName = 'LiveDotRing';

const LiveDotFill = styled('circle', {
  strokeWidth: 1,

  variants: {
    paused: {
      true: {
        fill: palette.bgNeutralLight,
        stroke: palette.strokeNeutralLight,

        [darkThemeSelector]: {
          fill: palette.bgNeutralDark,
          stroke: palette.strokeNeutralDark,
        },
      },
      false: {
        stroke: palette.strokePositiveLight,

        '@media (prefers-reduced-motion: reduce)': {
          fill: palette.green100,

          [darkThemeSelector]: {
            fill: palette.green400,
          },
        },
      },
    },
  },
});
LiveDotFill.displayName = 'LiveDotFill';

const LiveDotWaveInitial = styled('stop', {
  stopColor: palette.green50,

  [darkThemeSelector]: {
    stopColor: palette.green400,
  },
});
LiveDotWaveInitial.displayName = 'LiveDotWaveInitial';

const LiveDotWaveStage = styled('stop', {
  stopColor: palette.green100,

  [darkThemeSelector]: {
    stopColor: palette.green500,
  },
});
LiveDotWaveStage.displayName = 'LiveDotWaveStage';

export type LiveDotProps = {
  /**
   * Stops the pulse and draws the dot in neutral colors.
   */
  paused?: boolean;
  /**
   * The dot's diameter in `px`. Its ring spreads past this box without taking up layout space.
   */
  size?: number;
  /**
   * Positions the dot when it's nested inside another `<svg>`.
   */
  x?: number;
  /**
   * Positions the dot when it's nested inside another `<svg>`.
   */
  y?: number;
};

/**
 * A pulsing green dot for content that's updating live. Renders its own `<svg>`, so it can sit in
 * HTML or inside another `<svg>`, such as a chart.
 */
export function LiveDot({ paused = false, size = 12, x, y }: LiveDotProps) {
  const gradientId = useId();
  const radius = size / 2;

  return (
    <LiveDotSVG
      aria-hidden
      width={size}
      height={size}
      x={x}
      y={y}
      viewBox={`${-radius} ${-radius} ${size} ${size}`}
    >
      {!paused && (
        <defs>
          {/* Draws atto's positive background wave, which can't be a CSS background in SVG. */}
          <linearGradient id={gradientId} spreadMethod="repeat">
            <LiveDotWaveInitial offset={0} />
            <LiveDotWaveStage offset={0.5} />
            <LiveDotWaveInitial offset={1} />
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              values="0 0; -1 0"
              dur="1s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
      )}
      <LiveDotPulse paused={paused}>
        {!paused && <LiveDotRing r={radius} />}
        <LiveDotFill
          paused={paused}
          r={radius - 0.5}
          fill={paused ? undefined : `url(#${gradientId})`}
        />
      </LiveDotPulse>
    </LiveDotSVG>
  );
}
